import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Alert,
  Snackbar,
  Chip,
  Divider,
  CircularProgress,
  Tab,
  Tabs,
  InputAdornment,
  Fab,
} from '@mui/material';
import {
  PersonAdd as PersonAddIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Email as EmailIcon,
  Share as ShareIcon,
} from '@mui/icons-material';
import { Friend, UserSearchResult, FetchWithAuth } from '../../types';
import { API_URL } from '../../utils';
import { useLanguage } from '../../i18n/LanguageContext';
import ConfirmDeleteDialog from './ConfirmDeleteDialog';
import ShareWorkoutDialog from './ShareWorkoutDialog';
import SharedWorkoutsView from './SharedWorkoutsView';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`friends-tabpanel-${index}`}
      aria-labelledby={`friends-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

interface FriendsViewProps {
  fetchWithAuth: FetchWithAuth;
  notificationRefresh?: (() => Promise<void>) | null;
  initialTab?: number;
}

const FriendsView: React.FC<FriendsViewProps> = ({ fetchWithAuth, notificationRefresh, initialTab }) => {
  const { t } = useLanguage();
  const [tabValue, setTabValue] = useState(initialTab || 0);

  useEffect(() => {
    if (initialTab !== undefined) {
      setTabValue(initialTab);
    }
  }, [initialTab]);
  const [searchEmail, setSearchEmail] = useState('');
  const [searchResults, setSearchResults] = useState<UserSearchResult[]>([]);
  const [friends, setFriends] = useState<Friend[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [friendToRemove, setFriendToRemove] = useState<Friend | null>(null);
  const [shareWorkoutDialogOpen, setShareWorkoutDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'info',
  });

  const markFriendNotificationsAsRead = useCallback(async () => {
    try {
      await fetchWithAuth(`${API_URL}/notifications/mark-read-by-type`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ category: 'friends' }),
      });
      // Don't show success message as this is automatic behavior
      
      // Refresh notification badge with delay to prevent rapid calls
      if (notificationRefresh && typeof notificationRefresh === 'function') {
        setTimeout(() => {
          try {
            notificationRefresh();
          } catch (error) {
            console.error('Error calling notification refresh:', error);
          }
        }, 1000);
      }
    } catch (error) {
      console.error('Error marking friend notifications as read:', error);
      // Fail silently for better UX
    }
  }, [fetchWithAuth, notificationRefresh]);

  const loadFriends = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetchWithAuth(`${API_URL}/friends`);
      if (response.ok) {
        const data = await response.json();
        setFriends(Array.isArray(data) ? data : []);
      } else {
        throw new Error('Failed to load friends');
      }
    } catch (error) {
      console.error('Error loading friends:', error);
      showSnackbar(t('friends.loadError'), 'error');
    } finally {
      setLoading(false);
    }
  }, [fetchWithAuth, t]);

  // Load friends on mount and tab change
  useEffect(() => {
    loadFriends();
  }, [tabValue, loadFriends]);

  // Mark friend notifications as read on mount
  useEffect(() => {
    markFriendNotificationsAsRead();
  }, [markFriendNotificationsAsRead]);

  const searchUsers = async () => {
    if (!searchEmail.trim()) return;
    
    setSearchLoading(true);
    try {
      const response = await fetchWithAuth(`${API_URL}/friends/search?email=${encodeURIComponent(searchEmail.trim())}`);
      if (response.ok) {
        const data = await response.json();
        setSearchResults(Array.isArray(data) ? data : []);
      } else {
        throw new Error('Failed to search users');
      }
    } catch (error) {
      console.error('Error searching users:', error);
      showSnackbar(t('friends.searchError'), 'error');
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const sendFriendRequest = async (email: string) => {
    try {
      const response = await fetchWithAuth(`${API_URL}/friends/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      
      const data = await response.json();
      
      if (data.success) {
        showSnackbar(data.message, 'success');
        // Refresh search results to update status
        await searchUsers();
        await loadFriends();
      } else {
        showSnackbar(data.message, 'error');
      }
    } catch (error) {
      console.error('Error sending friend request:', error);
      showSnackbar(t('friends.sendRequestError'), 'error');
    }
  };

  const acceptFriendRequest = async (friendshipId: number) => {
    try {
      const response = await fetchWithAuth(`${API_URL}/friends/${friendshipId}/accept`, {
        method: 'POST',
      });
      
      const data = await response.json();
      
      if (data.success) {
        showSnackbar(data.message, 'success');
        await loadFriends();
      } else {
        showSnackbar(data.message, 'error');
      }
    } catch (error) {
      console.error('Error accepting friend request:', error);
      showSnackbar(t('friends.acceptRequestError'), 'error');
    }
  };

  const declineFriendRequest = async (friendshipId: number) => {
    try {
      const response = await fetchWithAuth(`${API_URL}/friends/${friendshipId}/decline`, {
        method: 'POST',
      });
      
      const data = await response.json();
      
      if (data.success) {
        showSnackbar(data.message, 'success');
        await loadFriends();
      } else {
        showSnackbar(data.message, 'error');
      }
    } catch (error) {
      console.error('Error declining friend request:', error);
      showSnackbar(t('friends.declineRequestError'), 'error');
    }
  };

  const removeFriend = async (friendshipId: number) => {
    try {
      const response = await fetchWithAuth(`${API_URL}/friends/${friendshipId}`, {
        method: 'DELETE',
      });
      
      const data = await response.json();
      
      if (data.success) {
        showSnackbar(data.message, 'success');
        await loadFriends();
        // Refresh search results if there are any
        if (searchResults.length > 0) {
          await searchUsers();
        }
      } else {
        showSnackbar(data.message, 'error');
      }
    } catch (error) {
      console.error('Error removing friend:', error);
      showSnackbar(t('friends.removeError'), 'error');
    } finally {
      setFriendToRemove(null);
    }
  };

  const handleRemoveFriendClick = (friend: Friend) => {
    setFriendToRemove(friend);
  };

  const handleConfirmRemoveFriend = () => {
    if (friendToRemove) {
      removeFriend(friendToRemove.id);
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const getStatusChip = (status: string, isRequester: boolean) => {
    switch (status) {
      case 'accepted':
        return <Chip label={t('friends.statusFriends')} color="success" size="small" />;
      case 'pending':
        return (
          <Chip
            label={isRequester ? t('friends.statusRequestSent') : t('friends.statusRequestReceived')}
            color={isRequester ? "info" : "warning"}
            size="small"
          />
        );
      case 'declined':
        return <Chip label={t('friends.statusDeclined')} color="error" size="small" />;
      case 'blocked':
        return <Chip label={t('friends.statusBlocked')} color="error" size="small" />;
      default:
        return null;
    }
  };

  const pendingRequests = friends.filter(f => f.status === 'pending' && !f.is_requester);
  const sentRequests = friends.filter(f => f.status === 'pending' && f.is_requester);
  const acceptedFriends = friends.filter(f => f.status === 'accepted');

  return (
    <Box sx={{ width: '100%', maxWidth: 800, mx: 'auto', p: 2 }}>
      <Typography variant="h4" component="h1" gutterBottom sx={{ mb: 3 }}>
        {t('friends.title')}
      </Typography>

      <Paper sx={{ width: '100%' }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="friends tabs"
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label={t('friends.friendsTab', { count: acceptedFriends.length })} />
          <Tab label={t('friends.requestsTab', { count: pendingRequests.length })} />
          <Tab label={t('friends.addFriendsTab')} />
          <Tab label={t('workoutShare.sharedWorkouts')} />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          {/* Friends List */}
          <Typography variant="h6" gutterBottom>
            {t('friends.yourFriends')}
          </Typography>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : acceptedFriends.length === 0 ? (
            <Alert severity="info">
              {t('friends.noFriendsMessage')}
            </Alert>
          ) : (
            <List>
              {acceptedFriends.map((friend) => (
                <ListItem key={friend.id}>
                  <ListItemText
                    primary={friend.friend_name}
                    secondary={friend.friend_email}
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      aria-label="remove friend"
                      onClick={() => handleRemoveFriendClick(friend)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          {/* Friend Requests */}
          <Typography variant="h6" gutterBottom>
            {t('friends.friendRequests')}
          </Typography>
          
          {/* Incoming Requests */}
          {pendingRequests.length > 0 && (
            <>
              <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
                {t('friends.receivedRequests')}
              </Typography>
              <List>
                {pendingRequests.map((friend) => (
                  <ListItem key={friend.id}>
                    <ListItemText
                      primary={friend.friend_name}
                      secondary={friend.friend_email}
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        aria-label="accept request"
                        onClick={() => acceptFriendRequest(friend.id)}
                        color="success"
                        sx={{ mr: 1 }}
                      >
                        <CheckIcon />
                      </IconButton>
                      <IconButton
                        edge="end"
                        aria-label="decline request"
                        onClick={() => declineFriendRequest(friend.id)}
                        color="error"
                      >
                        <CloseIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </>
          )}

          {/* Sent Requests */}
          {sentRequests.length > 0 && (
            <>
              {pendingRequests.length > 0 && <Divider sx={{ my: 2 }} />}
              <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
                {t('friends.sentRequests')}
              </Typography>
              <List>
                {sentRequests.map((friend) => (
                  <ListItem key={friend.id}>
                    <ListItemText
                      primary={friend.friend_name}
                      secondary={friend.friend_email}
                    />
                    <ListItemSecondaryAction>
                      <Chip label={t('friends.statusPending')} color="info" size="small" />
                      <IconButton
                        edge="end"
                        aria-label="cancel request"
                        onClick={() => handleRemoveFriendClick(friend)}
                        color="error"
                        sx={{ ml: 1 }}
                      >
                        <CloseIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </>
          )}

          {pendingRequests.length === 0 && sentRequests.length === 0 && (
            <Alert severity="info">
              {t('friends.noPendingRequests')}
            </Alert>
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          {/* Add Friends */}
          <Typography variant="h6" gutterBottom>
            {t('friends.findFriends')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <TextField
              fullWidth
              label={t('friends.searchByEmail')}
              variant="outlined"
              value={searchEmail}
              onChange={(e) => setSearchEmail(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && searchUsers()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="contained"
              onClick={searchUsers}
              disabled={!searchEmail.trim() || searchLoading}
              startIcon={searchLoading ? <CircularProgress size={20} /> : <SearchIcon />}
            >
              {t('friends.search')}
            </Button>
          </Box>

          {searchResults.length > 0 && (
            <>
              <Typography variant="subtitle1" gutterBottom>
                {t('friends.searchResults')}
              </Typography>
              <List>
                {searchResults.map((user) => (
                  <ListItem key={user.id}>
                    <ListItemText
                      primary={user.name}
                      secondary={user.email}
                    />
                    <ListItemSecondaryAction sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {user.friendship_status ? (
                        getStatusChip(user.friendship_status, user.is_requester || false)
                      ) : (
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<PersonAddIcon />}
                          onClick={() => sendFriendRequest(user.email)}
                        >
                          {t('friends.addFriend')}
                        </Button>
                      )}
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </>
          )}

          {searchEmail.trim() && searchResults.length === 0 && !searchLoading && (
            <Alert severity="info">
              {t('friends.noUsersFound')}
            </Alert>
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          {/* Shared Workouts */}
          <SharedWorkoutsView fetchWithAuth={fetchWithAuth} />
        </TabPanel>
      </Paper>

      {/* Floating Action Button for Share Workout */}
      {tabValue === 0 && acceptedFriends.length > 0 && (
        <Fab
          color="primary"
          aria-label="share workout"
          onClick={() => setShareWorkoutDialogOpen(true)}
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
          }}
        >
          <ShareIcon />
        </Fab>
      )}

      <ShareWorkoutDialog
        open={shareWorkoutDialogOpen}
        onClose={() => setShareWorkoutDialogOpen(false)}
        friends={friends}
        fetchWithAuth={fetchWithAuth}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {friendToRemove && (
        <ConfirmDeleteDialog
          open={!!friendToRemove}
          onClose={() => setFriendToRemove(null)}
          onConfirm={handleConfirmRemoveFriend}
          friendName={friendToRemove.friend_name}
        />
      )}
    </Box>
  );
};

export default FriendsView;